import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon, Logo } from '../../design/Icon';
import { fmt } from '../../design/format';
import { ThemeToggle } from '../../components/shared/Button';
import { Avatar } from '../../components/shared/People';
import { ErrorState } from '../../components/shared/States';
import { useAccountTimeZone, useOrgName } from '../../layout';
import { formatInZone, zonedFormat } from '../../design/timeZone';
import { usePolling, useSecondsSince } from '../../hooks/usePolling';
import { PRESENCE_CLASS, analyticsApi, clockTime } from '../../services/analyticsApi';
import './wallboard.css';

const POLL_MS = 15000;
const LONG_WAIT = 120;
const STATUS_ORDER = ['available', 'oncall', 'busy', 'away', 'offline'];
const STATUS_LABEL = { available: 'Available', oncall: 'On a call', busy: 'Busy', away: 'Away', offline: 'Offline' };
const SYNC_MINUTES = 15;

const longDate = (at) => formatInZone(at, { weekday: 'long', month: 'long', day: 'numeric' });
const timeWithSeconds = (at) => formatInZone(at, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
const localParts = (at) => zonedFormat('en-US', { hour: 'numeric', minute: 'numeric', hourCycle: 'h23', weekday: 'short' }).formatToParts(at);

const minutesOf = (hhmmss) => {
  const [h, m] = String(hhmmss).split(':').map(Number);
  return h * 60 + (m || 0);
};
const clockOf = (hhmmss) => {
  const minutes = minutesOf(hhmmss);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${((h + 11) % 12) + 1}:${m < 10 ? '0' : ''}${m} ${h < 12 ? 'AM' : 'PM'}`;
};

function nowMinutes(now) {
  const parts = Object.fromEntries(localParts(now).map((p) => [p.type, p.value]));
  return Number(parts.hour) * 60 + Number(parts.minute);
}

/** Today's business hours text ("8:00 AM to 6:00 PM") and whether the queues are open now. */
function hoursToday(data, now) {
  if (!data?.hoursConfigured) return { text: null, open: true };
  const intervals = data.todayHours || [];
  if (!intervals.length) return { text: null, open: false, closedAllDay: true };
  const first = intervals[0];
  const last = intervals[intervals.length - 1];
  const minute = nowMinutes(now);
  const open = intervals.some((i) => minute >= minutesOf(i.start) && minute < minutesOf(i.end));
  return { text: `${clockOf(first.start)} to ${clockOf(last.end)}`, open, end: clockOf(last.end), start: clockOf(first.start), before: minute < minutesOf(first.start) };
}

function Tile({ label, value, meta, tone, flash }) {
  return (
    <div className={`tile ${tone || ''}`.trim()} data-testid="wallboard-tile">
      <div className="tl">
        {tone === 'crit' && <Icon name="alert" className="ico-sm" />}
        {label}
      </div>
      <div className={`tv num${flash ? ' flash' : ''}`}>{value}</div>
      <div className="tm">{meta}</div>
    </div>
  );
}

function QueueCard({ q }) {
  const wait = q.lastHour.longestWaitSeconds;
  const below = q.serviceLevelCalls > 0 && q.serviceLevelPercent < q.targetPercent;
  const bad = wait > LONG_WAIT || below;
  return (
    <div className={`q${bad ? ' alert-q' : ''}`}>
      <div className="qn">
        <span className="qt" title={q.queueName}>
          {q.queueName}
        </span>
        {bad && (
          <span className="sev sev-immediate">
            <Icon name="alert" />
            Attention
          </span>
        )}
      </div>
      <div className="qs">
        <div>
          <b>{fmt.int(q.lastHour.offered)}</b>
          <span>Calls, 60 min</span>
        </div>
        <div>
          <b style={wait > LONG_WAIT ? { color: 'var(--critical)' } : undefined}>{wait ? fmt.mmss(wait) : '—'}</b>
          <span>Longest wait, 60 min</span>
        </div>
        <div>
          <b>{q.availableAgents}</b>
          <span>Available</span>
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="meter grow">
          <div className="fill" style={{ width: `${q.serviceLevelCalls ? q.serviceLevelPercent : 0}%`, background: below ? 'var(--critical-mark)' : 'var(--series-1)' }} />
          <div className="target" style={{ left: `${q.targetPercent}%` }} />
        </div>
        <span className="caption num">{q.serviceLevelCalls ? `SLA ${Number(q.serviceLevelPercent).toFixed(1)}%` : 'SLA —'}</span>
      </div>
      <div className="qf">
        {fmt.int(q.answeredToday)} answered today · {q.lastCallAt ? `last call ${clockTime(q.lastCallAt)}` : 'no calls today'}
      </div>
    </div>
  );
}

/**
 * Full-screen wallboard (mockup wallboard.html). Agent status is live from the Teams presence poll; call figures come
 * from synced call records (every 15 minutes), for the last 60 minutes and today, and the source line says so. Polls
 * every 15 seconds and pauses while the tab is hidden (usePolling). `?date=yyyy-mm-dd` shows that day's calls (local
 * verification).
 */
export function WallboardPage() {
  const org = useOrgName();
  const { label: zoneLabel } = useAccountTimeZone();
  const location = useLocation();
  const replayDate = useMemo(() => {
    const value = new URLSearchParams(location.search).get('date');
    return /^\d{4}-\d{2}-\d{2}$/.test(value || '') ? value : null;
  }, [location.search]);
  const [scope, setScope] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    document.title = 'Wallboard | Vertolytics';
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const params = useMemo(() => {
    const p = {};
    if (scope) p.hierarchyNodeIds = scope;
    if (replayDate) {
      p.from = replayDate;
      p.to = replayDate;
    }
    return p;
  }, [scope, replayDate]);

  const { data, error, lastUpdated, refresh } = usePolling(() => analyticsApi.wallboard(params), POLL_MS, { deps: [JSON.stringify(params)] });
  const seconds = useSecondsSince(lastUpdated);
  const stale = Boolean(error && data);
  const hours = hoursToday(data, now);
  const closed = Boolean(data && !replayDate && !hours.open);
  const dateLine = replayDate ? longDate(new Date(`${replayDate}T12:00:00Z`)) : longDate(now);
  const callsAsOf = data?.callsAsOf ? clockTime(data.callsAsOf) : null;

  const t = data?.tiles;
  const h = t?.lastHour;
  const counts = {};
  (data?.agents || []).forEach((a) => {
    counts[a.status] = (counts[a.status] || 0) + 1;
  });

  return (
    <div className={`wb${stale ? ' is-stale' : ''}`}>
      <header className="wb-head">
        <Link className="brand" to="/overview">
          <Logo size={26} />
          Vertolytics
        </Link>
        <div className="scope">
          <button className={`chip${!scope ? ' active' : ''}`} type="button" onClick={() => setScope(null)}>
            All {(data?.topLevelPluralName || 'Divisions').toLowerCase()}
          </button>
          {(data?.nodes || []).map((n) => (
            <button key={n.id} className={`chip${scope === n.id ? ' active' : ''}`} type="button" onClick={() => setScope(n.id)}>
              {n.name}
            </button>
          ))}
        </div>
        <span className="live">
          <span className="pulse" aria-hidden="true" />
          <span data-testid="relative-time">
            {stale
              ? `Connection lost · last update ${timeWithSeconds(lastUpdated)}`
              : seconds == null
                ? `Connecting · refreshes every ${POLL_MS / 1000} s`
                : `Updated ${seconds} s ago · refreshes every ${POLL_MS / 1000} s`}
          </span>
        </span>
        <div className="wb-clock">
          <div className="t" data-testid="live-clock">{clockTime(now)}</div>
          <div className="d">
            {dateLine}, {zoneLabel}
          </div>
        </div>
        <ThemeToggle />
        <Link className="btn" to="/overview">
          Exit wallboard
        </Link>
      </header>

      {stale && (
        <div className="alert alert-error" role="alert">
          <Icon name="alert" />
          <div>
            <div className="a-title">Live updates paused</div>
            <div>
              Vertolytics hasn't been able to refresh since {timeWithSeconds(lastUpdated)}. The figures below are {Math.max(1, Math.round(seconds / 60))} minute{Math.round(seconds / 60) > 1 ? 's' : ''} old. Retrying every 15 seconds.
            </div>
          </div>
        </div>
      )}

      {!data && error && <ErrorState title="Wallboard couldn't load" body="Vertolytics couldn't reach the analytics service. The wallboard retries every 15 seconds." error={error} onRetry={refresh} />}

      {closed && (
        <section className="state-panel">
          <div className="s-icon">
            <Icon name="moon" />
          </div>
          <h2>Queues are closed</h2>
          <p>
            {hours.closedAllDay
              ? `${org}'s queues are closed today. The wallboard resumes at the start of the next business day.`
              : hours.before
                ? `${org}'s business hours start at ${hours.start}. The wallboard resumes then.`
                : `${org}'s business hours ended at ${hours.end}. After-hours calls follow the after-hours routing in Teams. The wallboard resumes at ${hours.start}.`}
          </p>
        </section>
      )}

      {data && !closed && (
        <div className="wb-data stack" style={{ gap: 14 }}>
          <div className="wb-src" data-testid="wallboard-sources">
            <span>
              <b>Agents</b> live from Teams presence
            </span>
            <span>
              <b>Calls</b> from Teams call records, updated every {SYNC_MINUTES} minutes · {callsAsOf ? `last update ${callsAsOf}` : 'no calls synced yet'}
            </span>
          </div>
          <section className="tiles" aria-label="Today's totals">
            <Tile label="Available" value={fmt.int(t.available)} meta={`of ${t.signedIn} signed in`} />
            <Tile label="On a call" value={fmt.int(t.onCallsNow)} meta={`${t.busy} busy, ${t.away} away`} />
            <Tile label="Calls, last 60 min" value={fmt.int(h.offered)} meta={`${h.answered} answered, ${h.abandoned} abandoned`} />
            <Tile
              label="SLA, last 60 min"
              value={h.serviceLevelCalls ? `${Number(h.serviceLevelPercent).toFixed(1)}%` : '—'}
              meta={`${h.serviceLevelCalls} queued call${h.serviceLevelCalls === 1 ? '' : 's'}`}
              tone={h.serviceLevelCalls && h.serviceLevelPercent < t.target.answerTargetPercent ? 'crit' : ''}
            />
            <Tile label="Answered today" value={fmt.int(t.answeredToday)} meta={hours.start ? `since ${hours.start}` : 'today'} />
            <Tile
              label="Abandoned today"
              value={fmt.int(t.abandonedToday)}
              meta={`${Number(t.abandonPercent).toFixed(1)}% of offered`}
              tone={Number(t.abandonPercent) > Number(t.target.abandonTargetPercent) ? 'warn' : ''}
            />
            <Tile
              label="SLA today"
              value={t.serviceLevelCalls ? `${Number(t.serviceLevelPercent).toFixed(1)}%` : '—'}
              meta={`target ${Number(t.target.answerTargetPercent)}% in ${t.target.answerWithinSeconds} s`}
              tone={t.serviceLevelCalls && t.serviceLevelPercent < t.target.answerTargetPercent ? 'crit' : ''}
            />
          </section>
          <div className="wb-main">
            <section className="card">
              <div className="card-head">
                <div>
                  <div className="t">Queues</div>
                  <div className="d">
                    Last 60 minutes and today
                    {hours.text ? `, business hours ${hours.text}` : ''}
                  </div>
                </div>
                <div className="acts legend">
                  <span>
                    <span className="dot" style={{ background: 'var(--critical-mark)' }} />
                    Wait over 2:00 or SLA under target
                  </span>
                </div>
              </div>
              <div className="card-body">
                {data.queues.length ? (
                  <div className="qgrid">
                    {data.queues.map((q) => (
                      <QueueCard key={q.queueId} q={q} />
                    ))}
                  </div>
                ) : (
                  <div className="caption">No queues in this scope.</div>
                )}
              </div>
            </section>
            <div className="stack" style={{ gap: 14 }}>
              <section className="card">
                <div className="card-head">
                  <div>
                    <div className="t">Agents</div>
                    <div className="d">
                      {t.signedIn} signed in{data.presenceAsOf ? '' : ' · presence not available yet'}
                    </div>
                  </div>
                  <div className="acts legend">
                    {STATUS_ORDER.map((s) => (
                      <span key={s}>
                        <span className={`presence p-${PRESENCE_CLASS[s]}`} />
                        {STATUS_LABEL[s]} {counts[s] || 0}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="card-body">
                  <div className="agrid">
                    {data.agents.map((a) => (
                      <div className="ag" key={a.agentId}>
                        <span style={{ position: 'relative' }}>
                          <Avatar name={a.name} photoId={a.agentId} />
                          <span className={`presence p-${PRESENCE_CLASS[a.status] || 'offline'}`} style={{ position: 'absolute', right: -2, bottom: -2 }} />
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div className="an">{a.name}</div>
                          <div className="as">
                            {a.label}
                            {a.status === 'offline' ? '' : ` · ${a.handledToday} today`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
              <section className="card">
                <div className="card-head">
                  <div>
                    <div className="t">Alerts</div>
                    <div className="d">Last 60 minutes</div>
                  </div>
                </div>
                <div className="card-body">
                  {data.alerts.length ? (
                    <ul className="ticker">
                      {data.alerts.map((a, i) => (
                        <li key={`${a.at}-${i}`}>
                          <span className={`sev ${a.level === 'critical' ? 'sev-immediate' : a.level === 'serious' ? 'sev-48h' : 'sev-info'}`}>
                            <Icon name={a.level === 'info' ? 'info' : 'alert'} />
                          </span>
                          <span className="grow">{a.text}</span>
                          <span className="caption nowrap">{clockTime(a.at)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="caption">No alerts in the last 60 minutes.</div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
